import { memo } from 'react';
import { ModpackNote } from '../types/modpackNote';
import { MarkdownRenderer } from './MarkdownRenderer';
import { CreatorProfile } from './CreatorProfile';
import { Server, Globe, Pencil, Trash2, Clock, Info } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '../utils/cn';
import { motion } from 'framer-motion';

interface ModpackNoteCardProps {
  note: ModpackNote;
  index: number;
  clusterThumbnails: Record<string, string>;
  canEdit: boolean;
  canDelete: boolean;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const ModpackNoteCard = memo(({
  note,
  index,
  clusterThumbnails,
  canEdit,
  canDelete,
  onClick,
  onEdit,
  onDelete,
}: ModpackNoteCardProps) => {
  const thumbnailUrl = note.cluster_id ? clusterThumbnails[note.cluster_id] : undefined;

  // Truncate content for preview (first ~200 chars)
  const previewContent = note.content.length > 200
    ? note.content.slice(0, 200) + '…'
    : note.content;

  const hasMetadata = note.metadata !== null && note.metadata !== '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03, duration: 0.25 }}
      className="glass-card p-4 md:p-5 cursor-pointer hover:bg-glass-200/10 transition-colors group"
      onClick={onClick}
    >
      {/* Header: Association badges + timestamp */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center flex-wrap gap-2">
          {note.cluster_id && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-500/15 text-purple-300 border border-purple-500/25">
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={note.cluster_id}
                  className="w-4 h-4 rounded-sm object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <Server className="w-3 h-3" />
              )}
              {note.cluster_id}
            </span>
          )}
          {note.fqdn && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-accent-500/15 text-accent-300 border border-accent-500/25">
              <Globe className="w-3 h-3" />
              {note.fqdn}
            </span>
          )}
          {hasMetadata && (
            <span
              className="badge bg-amber-500/10 text-amber-400 border border-amber-500/20"
              title="Has metadata"
            >
              <Info className="w-3 h-3" />
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-glass-400 whitespace-nowrap">
            {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
          </span>
        </div>
      </div>

      {/* Content preview */}
      <div className="mb-3 text-sm line-clamp-3 overflow-hidden">
        <MarkdownRenderer content={previewContent} className="[&_p]:my-0.5 [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm" />
      </div>

      {/* Footer: Creator + actions */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-xs text-glass-400 flex items-center gap-1.5">
            <span className="text-glass-500">by</span>
            <CreatorProfile creatorId={note.creator_id} inline />
          </div>
          {note.updated_at && (
            <div className="text-xs text-glass-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>edited {formatDistanceToNow(new Date(note.updated_at), { addSuffix: true })}</span>
              {note.updated_by && (
                <>
                  <span>by</span>
                  <CreatorProfile creatorId={note.updated_by} inline />
                </>
              )}
            </div>
          )}
        </div>
        <div className={cn(
          "flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity",
          "max-md:opacity-100" // Always visible on mobile
        )}>
          {canEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="p-1.5 rounded-md hover:bg-glass-200/20 text-glass-400 hover:text-accent-300 transition-colors touch-manipulation"
              title="Edit note"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-1.5 rounded-md hover:bg-red-500/15 text-glass-400 hover:text-red-400 transition-colors touch-manipulation"
              title="Delete note"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
});

ModpackNoteCard.displayName = 'ModpackNoteCard';
